import { useState, useEffect } from "react";
import { TemplateEditorPanel } from "@components";
import PreviewCustomerSelector from "./PreviewCustomerSelector";
import PreviewFooter from "./PreviewFooter";

export default function PreviewEditorStep({
  control,
  errors,
  watch,
  getValues,
  setValue,
  merchant,
  activeTab,
  setActiveTab,
  previewMode,
  emailEnabled,
  smsEnabled,
  invoicesRequested,
  watchedMandatory,
  invoiceEdited,
  pdfEdited,
  cardEdited,
  paymentProfileEdited,
  channelEdited,
  isEdited,
  hasUnappliedChanges,
  totalAmount,
  previewTokens,
  preparedRows,
  previewCustomerIdx,
  onPreviewCustomerChange,
  onToggleChannel,
  onBack,
  onResetEdits,
  onSend,
  onEnterEdit,
  onCancelEdit,
  onDoneEdit,
  onResetInEditMode,
  onSaveAsTemplate,
  canEditTemplates = true,
  isSingleCustomer,
  hideBack = false,
  readOnly = false,
  onClose,
}) {
  const customAmount = watch("customAmount");
  const smsText = watch("smsText");
  const emailSubject = watch("emailSubject");
  const isCustom = invoicesRequested === "Custom";
  // Specific invoices are request-scoped (their numbers are resolved per request and aren't stored on
  // a template), so this send can't be saved as a reusable template — hide the Save As action.
  const isSpecific = invoicesRequested === "SpecificInvoices";
  const [customAmountError, setCustomAmountError] = useState(null);

  useEffect(() => {
    if (customAmountError && parseFloat(customAmount) >= 0.3) {
      setCustomAmountError(null);
    }
  }, [customAmount, customAmountError]);

  const handleReview = () => {
    if (isCustom) {
      const num = parseFloat(customAmount);
      if (!customAmount || !Number.isFinite(num) || num < 0.3) {
        setCustomAmountError(
          !customAmount ? "Please enter an amount" : "Minimum amount is £0.30",
        );
        return;
      }
    }
    onSend();
  };

  const accountLabel = `${preparedRows?.length} ${preparedRows?.length === 1 ? "account" : "accounts"}`;

  return (
    <TemplateEditorPanel
      control={control}
      errors={errors}
      watch={watch}
      getValues={getValues}
      setValue={setValue}
      merchant={merchant}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      emailEnabled={emailEnabled}
      smsEnabled={smsEnabled}
      previewMode={previewMode}
      onToggleChannel={onToggleChannel}
      onEnterEdit={readOnly ? undefined : onEnterEdit}
      disableCustom={!isSingleCustomer}
      dropdownEditBadges={{
        invoice: invoiceEdited,
        pdf: pdfEdited,
        card: cardEdited,
        paymentProfile: paymentProfileEdited,
        channel: channelEdited,
      }}
      previewTokens={previewTokens}
      previewCustomerSelector={
        <PreviewCustomerSelector
          preparedRows={preparedRows}
          previewCustomerIdx={previewCustomerIdx}
          onPreviewCustomerChange={onPreviewCustomerChange}
        />
      }
      previewCustomerRow={preparedRows?.[previewCustomerIdx]}
      invoicesRequested={invoicesRequested}
      mandatory={watchedMandatory}
      showEmptyState={false}
      renderFooter={() => (
        <PreviewFooter
          previewMode={previewMode}
          isCustom={isCustom}
          accountLabel={accountLabel}
          control={control}
          customAmountError={customAmountError}
          invoicesRequested={invoicesRequested}
          totalAmount={totalAmount}
          smsEnabled={smsEnabled}
          emailEnabled={emailEnabled}
          isEdited={isEdited}
          hasUnappliedChanges={hasUnappliedChanges}
          getValues={getValues}
          smsText={smsText}
          emailSubject={emailSubject}
          onBack={onBack}
          hideBack={hideBack}
          onReview={handleReview}
          onCancelEdit={onCancelEdit}
          onResetInEditMode={onResetInEditMode}
          onSaveAsTemplate={onSaveAsTemplate}
          canEditTemplates={canEditTemplates && !isSpecific}
          onDoneEdit={onDoneEdit}
          readOnly={readOnly}
          onClose={onClose}
        />
      )}
    />
  );
}
